import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Analytics, identifyUser } from "@/utils/analytics";

export default function AuthCallback() {
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef for processed flag - set synchronously to prevent race conditions under StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error("No session_id found in URL");
          window.location.href = "/login";
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Clear the URL fragment FIRST to prevent re-processing
        window.history.replaceState(null, "", window.location.pathname);
        
        // Exchange session_id for session_token
        const result = await login(sessionId);
        
        // Track successful login and identify user
        Analytics.loginCompleted('google');
        if (result.user) {
          identifyUser(result.user.user_id, {
            email: result.user.email,
            name: result.user.name,
            is_admin: result.user.is_admin
          });
        }
        
        // Use full page navigation to ensure auth state is fresh
        // New users go to Solutions page (dashboard would be empty)
        // Returning users also go to Solutions as main entry point
        if (result.has_business_profile) {
          window.location.href = "/solutions";
        } else {
          window.location.href = "/verify-business";
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        // Clear hash on error too
        window.history.replaceState(null, "", window.location.pathname);
        
        // Check if error is due to personal email rejection
        if (error.response?.status === 403) {
          Analytics.loginFailed('personal_email_blocked');
          toast.error(error.response?.data?.detail || "Personal email addresses are not allowed. Please use your corporate email.");
        } else {
          Analytics.loginFailed('auth_error');
          toast.error("Authentication failed. Please try again.");
        }
        window.location.href = "/login";
      }
    };

    processAuth();
  }, [location.hash, login]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
}
